import { hasTemplate, parse, type Expression } from '@de/utils/interpolation';

const SCOPE_ROOT = 'scope';

/**
 * The part of `scope` that templates inside `value` read. A template that
 * reads the whole bag (or walks it with a wildcard) gets all of it. Keys a
 * template names that are not in `scope` stay absent, so they still read
 * as unpublished.
 */
export function scopeReadBy(
  value: unknown,
  scope: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  const reads = new Set<string>();
  if (collectScopeReads(value, reads, new WeakSet<object>())) return scope;
  const subset: Record<string, unknown> = {};
  reads.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(scope, key)) {
      subset[key] = scope[key];
    }
  });
  return subset;
}

/** Whether a template names a top-level scope key absent from the bag. */
export function readsMissingScopeKey(
  value: unknown,
  scope: Readonly<Record<string, unknown>>,
): boolean {
  const reads = new Set<string>();
  if (collectScopeReads(value, reads, new WeakSet<object>())) return false;
  let missing = false;
  reads.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(scope, key)) missing = true;
  });
  return missing;
}

/** Adds read keys to `reads`; returns true when the whole bag is read. */
function collectScopeReads(
  value: unknown,
  reads: Set<string>,
  stack: WeakSet<object>,
): boolean {
  if (typeof value === 'string') {
    if (!hasTemplate(value)) return false;
    let segments;
    try {
      segments = parse(value).segments;
    } catch {
      return false;
    }
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      if (segment.type === 'expression' && expressionReadsAll(segment, reads)) {
        return true;
      }
    }
    return false;
  }
  if (typeof value !== 'object' || value === null) return false;
  if (stack.has(value)) return false;
  stack.add(value);
  const children = Array.isArray(value)
    ? value
    : Object.keys(value).map((key) => (value as Record<string, unknown>)[key]);
  for (let i = 0; i < children.length; i++) {
    if (collectScopeReads(children[i], reads, stack)) return true;
  }
  return false;
}

function expressionReadsAll(
  expression: Expression,
  reads: Set<string>,
): boolean {
  if (expression.arg && expressionReadsAll(expression.arg, reads)) return true;
  const [root, key] = expression.segments;
  if (root?.type !== 'key' || root.name !== SCOPE_ROOT) return false;
  if (key?.type !== 'key') return true;
  reads.add(key.name);
  return false;
}
